import { useTheme } from 'next-themes';
import { Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

// =====================================================================
// iSentinel360 v0.4.1 — PWA ThemeSwitcher (v6, with direct bridge call)
//
// === What changed in v6 ===
//
// v4 fixed the "double-click" bug by switching from `theme` (the
// setting, which can be "system") to `resolvedTheme` (the actually-
// applied theme).
//
// v6 adds a SECOND, more direct path: when the user clicks the
// toggle, this component calls `window.Android.setStatusBarBackground()`
// and `window.Android.setStatusBarIcons()` immediately, before
// `next-themes` even commits the new theme to the DOM. This is
// bulletproof against any `useEffect`/observer timing issues —
// the user clicked, so we know the theme changed, so we tell the
// native side right now.
//
// It also dispatches a custom event `iSentinel360:theme-changed` so
// other parts of the PWA (or the useThemeSync hook as a fallback)
// can listen for the same signal.
//
// === What stays the same ===
//
// The `resolvedTheme` toggle logic from v4 is preserved, so the
// "double-click" bug stays fixed.
//
// === How to verify in logcat ===
//
//   adb logcat -s "iSentinel360" "iSentinel360.JS" "chromium"
//
// You should see on every click:
//
//   [PWA console] ... [ThemeSwitcher] clicked, current isDark=true → flipping to light
//   [PWA console] ... [ThemeSwitcher] dispatched custom event "iSentinel360:theme-changed"
//   [PWA console] ... [ThemeSwitcher] direct bridge call: setStatusBarBackground(#FFFFFF), setStatusBarIcons(light)
//
// And on the native side:
//
//   setStatusBarBackground: #FFFFFF
//   setStatusBarIcons: mode=light → isAppearanceLightStatusBars=true
// =====================================================================

const STATUS_BAR_HEX = {
  light: '#FFFFFF',
  dark: '#020617',
} as const;

export default function ThemeSwitcher() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  const isDark = resolvedTheme === 'dark';

  const handleToggle = () => {
    const newTheme = isDark ? 'light' : 'dark';
    console.log(`[ThemeSwitcher] clicked, current isDark=${isDark} → flipping to "${newTheme}"`);

    // 1) Tell next-themes to apply the new theme. This updates
    //    <html class="dark"> (or removes it) and re-renders any
    //    components that read resolvedTheme.
    setTheme(newTheme);

    // 2) Dispatch a custom event so other code (including the
    //    useThemeSync hook as a fallback) can react immediately.
    try {
      window.dispatchEvent(new CustomEvent('iSentinel360:theme-changed', {
        detail: { theme: newTheme },
      }));
      console.log('[ThemeSwitcher] dispatched custom event "iSentinel360:theme-changed"');
    } catch (e) {
      console.warn('[ThemeSwitcher] failed to dispatch custom event:', e);
    }

    // 3) Directly call the native bridge. This is the bulletproof
    //    path — we know the user just changed the theme, so we
    //    tell the native side right now, no waiting for React's
    //    commit phase or next-themes' useEffect.
    const android = (window as any).Android;
    if (android) {
      const hex = newTheme === 'dark' ? STATUS_BAR_HEX.dark : STATUS_BAR_HEX.light;
      try {
        android.setStatusBarBackground(hex);
        android.setStatusBarIcons(newTheme);
        console.log(`[ThemeSwitcher] direct bridge call: setStatusBarBackground(${hex}), setStatusBarIcons(${newTheme})`);
      } catch (e) {
        console.error('[ThemeSwitcher] direct bridge call threw:', e);
      }
    } else {
      console.log('[ThemeSwitcher] window.Android is undefined — PWA is in a regular browser, not the APK');
    }
  };

  return (
    <button
      onClick={handleToggle}
      className="p-2 rounded-md hover:bg-accent transition-colors"
      title="Toggle theme"
    >
      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
}
